import { getSlotSessionDescription } from "./registrationAppointmentTimeslots";
import { getStoredPaymentTypeFields } from "../registrationPaymentType";
import {
  isInPersonModality,
  mapSlotLocationToLabel,
  MODALITY_LABELS,
} from "../Dietitian/registrationMatchedRd";
import i18n from "i18next";
import {
  formatAppointmentDateTime,
  getAppointmentConfirmationNumber,
  getAppointmentDietitianName,
} from "./calendlyBooking";

const isRegistrationTranslationKey = (value) =>
  typeof value === "string" && /^NUTRITION_[A-Z0-9_]+$/.test(value.trim());

const DEFAULT_SESSION_DURATION_MINUTES = 50;
const CARD_ON_FILE_LABEL_KEY = "NUTRITION_CONFIRM_VAL_CARD_ON_FILE";

const getDefaultCalendlyLocation = (registrationData) => {
  const rd =
    registrationData?.selectedDietitian?.raw ??
    registrationData?.noPreferenceSelectedRd?.raw ??
    null;
  const urls = rd?.rd_calendly_urls;
  if (!Array.isArray(urls)) {
    return "";
  }
  const defaultCal =
    urls.find((item) => item?.is_default === true || item?.is_default === 1) ??
    null;
  return defaultCal?.location ?? "";
};

const resolveSessionDurationMinutes = (appointment) => {
  const candidates = [
    appointment?.duration_minutes,
    appointment?.durationMinutes,
    appointment?.duration,
  ];

  for (const value of candidates) {
    const parsed = Number(value);
    if (!Number.isNaN(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return DEFAULT_SESSION_DURATION_MINUTES;
};

const buildSessionFormatLabel = (durationMinutes, modalityLabel) => {
  if (isInPersonModality(modalityLabel)) {
    return i18n.t("NUTRITION_CONFIRM_VAL_FORMAT_IN_PERSON");
  }
  return i18n.t("NUTRITION_CONFIRM_LBL_FORMAT_VALUE");
};

const resolveConfirmationFormatDisplay = (format, formatFallback) => {
  const trimmed = typeof format === "string" ? format.trim() : "";
  if (trimmed) {
    if (isRegistrationTranslationKey(trimmed)) {
      return i18n.t(trimmed);
    }
    return trimmed;
  }
  return formatFallback || "";
};

const formatRdNameFromRaw = (raw) => {
  if (!raw || typeof raw !== "object") {
    return "";
  }
  const firstName = raw.first_name ?? raw.firstName ?? "";
  const lastName = raw.last_name ?? raw.lastName ?? "";
  return `${firstName} ${lastName}`.trim();
};

const resolveSelectedDietitianDisplay = (registrationData) => {
  const { selectedDietitian, noPreferenceSelectedRd, matchedRdList } =
    registrationData;

  if (!selectedDietitian) {
    return "";
  }

  const rdId =
    selectedDietitian.id ??
    selectedDietitian.raw?.id ??
    noPreferenceSelectedRd?.rdId;

  const isUnassignedNoPreference =
    selectedDietitian.isNoPreference || String(rdId) === "no-preference";

  if (isUnassignedNoPreference) {
    return "";
  }

  const fromNoPreferenceView =
    noPreferenceSelectedRd?.view?.fullName ??
    noPreferenceSelectedRd?.view?.name;
  if (fromNoPreferenceView) {
    return fromNoPreferenceView;
  }

  const fromNoPreferenceRaw = formatRdNameFromRaw(noPreferenceSelectedRd?.raw);
  if (fromNoPreferenceRaw) {
    return fromNoPreferenceRaw;
  }

  if (Array.isArray(matchedRdList)) {
    const matched = matchedRdList.find(
      (rd) => String(rd?.id ?? rd?.rd_id) === String(rdId)
    );
    const fromMatchedRaw = formatRdNameFromRaw(matched);
    if (fromMatchedRaw) {
      return fromMatchedRaw;
    }
    const fromMatchedName = matched?.name ?? matched?.fullName;
    if (fromMatchedName) {
      return fromMatchedName;
    }
  }

  const fromSelectedRaw = formatRdNameFromRaw(selectedDietitian.raw);
  if (fromSelectedRaw) {
    return fromSelectedRaw;
  }

  return selectedDietitian.name || "";
};

export const getAppointmentFormatLabel = (registrationData, appointment) => {
  const durationMinutes = resolveSessionDurationMinutes(appointment);
  const slotLocation = registrationData?.selectedAppointment?.location;
  const defaultLocation = getDefaultCalendlyLocation(registrationData);
  const modalityLabel = mapSlotLocationToLabel(
    typeof slotLocation === "string" && slotLocation.trim()
      ? slotLocation
      : defaultLocation
  );

  if (modalityLabel) {
    return buildSessionFormatLabel(durationMinutes, modalityLabel);
  }

  const fromSlotDescription = getSlotSessionDescription(
    registrationData?.selectedAppointment,
    registrationData?.appointmentWeek
  );
  if (fromSlotDescription) {
    const normalized = fromSlotDescription.toLowerCase();
    if (normalized.includes("in-person") || normalized.includes("in person")) {
      return buildSessionFormatLabel(durationMinutes, MODALITY_LABELS.IN_PERSON);
    }
  }

  return buildSessionFormatLabel(durationMinutes, "Telehealth");
};

export const getConfirmationPaymentDisplay = (registrationData = {}) => {
  const { paymentType } = getStoredPaymentTypeFields(registrationData);
  const isInsurance =
    paymentType === "insurance" || Boolean(registrationData.coverageResult);

  if (isInsurance) {
    return i18n.t("NUTRITION_CONFIRM_VAL_PAYMENT_INSURANCE");
  }
  if (paymentType === "selfpay") {
    return i18n.t("NUTRITION_CONFIRM_VAL_PAYMENT_SELF_PAY");
  }
  return "";
};

export const getBookingDetailsFromAppointment = (appointment) => {
  if (!appointment || typeof appointment !== "object") {
    return null;
  }

  const details = appointment.booking_details ?? appointment.bookingDetails;
  return details && typeof details === "object" ? details : null;
};

export const buildBookingDetailsPaymentDisplay = (payment) => {
  const trimmed = typeof payment === "string" ? payment.trim() : "";
  if (!trimmed) {
    return "";
  }

  if (/card on file/i.test(trimmed)) {
    return trimmed;
  }

  return `${trimmed} · ${i18n.t(CARD_ON_FILE_LABEL_KEY)}`;
};

export const buildConfirmationSummary = (registrationData, appointment = null) => {
  const { selectedAppointment } = registrationData;
  const bookingDetails = getBookingDetailsFromAppointment(appointment);

  const dietitianFallback = resolveSelectedDietitianDisplay(registrationData);
  const dateTimeFallback = selectedAppointment
    ? `${selectedAppointment.day} · ${selectedAppointment.time}`
    : "";
  const paymentFallback = getConfirmationPaymentDisplay(registrationData);
  const formatFallback = getAppointmentFormatLabel(registrationData, appointment);
  const confirmationNumber =
    getAppointmentConfirmationNumber(appointment) ??
    registrationData.confirmationNumber ??
    null;

  if (!bookingDetails) {
    return {
      dietitianDisplay:
        getAppointmentDietitianName(appointment, dietitianFallback) ||
        dietitianFallback,
      dateTimeDisplay:
        formatAppointmentDateTime(appointment, dateTimeFallback) ||
        dateTimeFallback,
      paymentDisplay: paymentFallback,
      formatDisplay: formatFallback,
      confirmationNumber,
    };
  }

  const dietitian =
    bookingDetails.dietitian ??
    bookingDetails.dietitian_name ??
    bookingDetails.dietitianName ??
    "";
  const dateTime =
    bookingDetails.date_time ??
    bookingDetails.dateTime ??
    "";
  const payment = bookingDetails.payment ?? "";
  const format = bookingDetails.format ?? "";

  return {
    dietitianDisplay:
      dietitian ||
      getAppointmentDietitianName(appointment, dietitianFallback) ||
      dietitianFallback,
    dateTimeDisplay:
      (typeof dateTime === "string" && dateTime.trim()) ||
      formatAppointmentDateTime(appointment, dateTimeFallback) ||
      dateTimeFallback,
    paymentDisplay:
      paymentFallback ||
      buildBookingDetailsPaymentDisplay(payment),
    formatDisplay: resolveConfirmationFormatDisplay(format, formatFallback),
    confirmationNumber,
  };
};
